import React from 'react';
import {
  Container,
  Typography,
  Box,
  Toolbar,
  useTheme,
  useMediaQuery,
  BoxProps,
  TypographyProps,
  Button,
  ButtonProps,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { TP_COLORS } from '../../constants';
import Footer from '../V2/Portal/components/Footer';
import Header from '../V2/Portal/components/Header';

const boxStyles: BoxProps['sx'] = {
  borderRadius: '20px',
  padding: '30px',
  boxShadow: '0px 14px 40px 0px #6E9DB22E',
  alignSelf: 'center',
  marginBottom: '20px',
};

const imgBoxStyles: BoxProps['sx'] = {
  background: `
      url('${process.env.PUBLIC_URL}/images/blogPage/hat.png') top 5% left 0px / 200px 300px no-repeat,
      url('${process.env.PUBLIC_URL}/images/blogPage/pen.png') top 20% left 0px / 200px 300px no-repeat,
      url('${process.env.PUBLIC_URL}/images/blogPage/book.png') top 35% left 0px / 200px 300px no-repeat,
      url('${process.env.PUBLIC_URL}/images/blogPage/ruler.png') top 55% left 35px / 300px 300px no-repeat,
      url('${process.env.PUBLIC_URL}/images/blogPage/calculator.png') top 75% left 0px / 200px 300px no-repeat,
      url('${process.env.PUBLIC_URL}/images/blogPage/medal.png') top 90% left 15px / 300px 300px no-repeat,
      url('${process.env.PUBLIC_URL}/images/blogPage/ruler.png') top 10% right 0px / 300px 300px no-repeat,
      url('${process.env.PUBLIC_URL}/images/blogPage/calculator2.png') top 25% right 0px / 300px 300px no-repeat,
      url('${process.env.PUBLIC_URL}/images/blogPage/medal.png') top 40% right 0px / 300px 300px no-repeat,
      url('${process.env.PUBLIC_URL}/images/blogPage/hat2.png') top 60% right 35px / 300px 300px no-repeat,
      url('${process.env.PUBLIC_URL}/images/blogPage/pen2.png') top 80% right 0px / 200px 300px no-repeat,
      url('${process.env.PUBLIC_URL}/images/blogPage/book2.png') top 97% right 35px / 300px 300px no-repeat`,
};

const btnBoxStyles: BoxProps['sx'] = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
};

const liProps: TypographyProps = {
  component: 'li',
  sx: {
    fontSize: '14px',
    paddingBottom: '5px',
  },
};

const liContentProps: TypographyProps = {
  sx: {
    fontSize: '14px',
    color: '#647488',
    paddingBottom: '15px',
  },
};

const btnProps: ButtonProps = {
  sx: {
    backgroundColor: '#1E293B',
    color: 'white',
    fontSize: '14px',
    borderRadius: '50px',
    pl: 3,
    pr: 3,
    mt: 1,
    mb: 3,
    '&:hover': {
      backgroundColor: '#787878',
    },
  },
};

const BlogPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const belowMd = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <>
      <Header />
      <Toolbar />
      <Box
        sx={{
          background: `url('${process.env.PUBLIC_URL}/images/backgrounds/bg-courses-grid.png') 0px -15px`,
          backgroundRepeat: 'no-repeat',
        }}
      >
        <Box sx={!belowMd ? imgBoxStyles : null}>
          <Container maxWidth="md">
            <Typography
              variant="h5"
              align="center"
              gutterBottom
              style={{
                paddingTop: '5%',
                fontWeight: 'bold',
                marginBottom: '25px',
              }}
            >
              <span style={{ color: TP_COLORS.blue.dark }}>
                Unlock Your Exam Success with
              </span>
              <span style={{ color: TP_COLORS.tutopiyaGreenMain.darken }}>
                {' '}
                Topical Past Papers
              </span>
              <span style={{ color: TP_COLORS.blue.dark }}>
                :<br />A Game-Changer for Students
              </span>
            </Typography>
            <Typography
              align="center"
              gutterBottom
              style={{ marginBottom: '25px', fontSize: '15px' }}
            >
              Practice makes perfect. This age-old adage is not just a saying;
              it’s a proven formula for success. The more we practice, the
              clearer our understanding becomes. And clarity? That’s the
              foundation of mastery in any subject. But how can students achieve
              this level of clarity across diverse topics, especially when
              traditional methods often fall short?
            </Typography>
            <Typography
              align="center"
              gutterBottom
              style={{ marginBottom: '40px', fontSize: '15px' }}
            >
              We’re thrilled to announce the launch of Topical Past Papers on
              our learning platform—your ultimate tool to revolutionize exam
              preparation and ensure mastery of every chapter in your
              curriculum.
            </Typography>
            <Box display="flex" flexDirection="column" sx={boxStyles}>
              <Typography fontWeight="bold" fontSize="17px">
                The Challenge: Superficial Learning vs. In-Depth Mastery
              </Typography>
              <Typography color="#647488" fontSize="14px" paddingBottom="15px">
                <br />
                When preparing for exams, many students focus on covering the
                entire syllabus rather than truly understanding each chapter.
                While this may seem like an efficient strategy, it often leads
                to shallow comprehension.
                <br /> <br /> Take Mathematics as an example. The subject spans
                diverse topics like Numbers, Coordinate Geometry, Vectors,
                Transformations, Algebra, and Probability. To excel, one must
                not only understand these chapters but also practice solving a
                variety of problems that challenge and expand their knowledge.
                <br /> <br /> However, relying solely on past papers or textbook
                exercises poses significant challenges:
              </Typography>
              <Typography component="ul" style={{ margin: '0' }}>
                <Typography {...liProps}>Limited Variety:</Typography>
                <Typography {...liContentProps}>
                  Traditional past papers include only a handful of questions
                  from each chapter, offering an incomplete picture.
                </Typography>
                <Typography {...liProps}>Repetitive Exercises:</Typography>
                <Typography {...liContentProps}>
                  Textbook questions can become monotonous and fail to reflect
                  the complexity and variety seen in actual exams.
                </Typography>
                <Typography {...liProps}>Misaligned Preparation:</Typography>
                <Typography {...liContentProps}>
                  Students often miss out on crucial concepts or struggle to
                  identify the most common and impactful questions.
                </Typography>
              </Typography>
              <Typography color="#647488" fontSize="14px">
                This gap in preparation can make it difficult for students to
                achieve the level of mastery required to excel in exams.
              </Typography>
            </Box>
            <Box display="flex" flexDirection="column" sx={boxStyles}>
              <Typography fontWeight="bold" fontSize="17px">
                The Solution: Topical Past Papers
              </Typography>
              <Typography color="#647488" fontSize="14px" paddingBottom="15px">
                <br />
                Enter Topical Past Papers, a resource designed to address these
                gaps and transform how students prepare. These papers
                consolidate questions from past exams, categorizing them by
                chapter and sub-topic. <br /> Here’s why they’re a game-changer:
              </Typography>
              <Typography component="ul" style={{ margin: '0' }}>
                <Typography {...liProps}>Comprehensive Coverage:</Typography>
                <Typography {...liContentProps}>
                  Each paper contains a wide range of questions from a single
                  chapter, ensuring that students encounter every possible type
                  of question.
                </Typography>
                <Typography {...liProps}>Curriculum Alignment: </Typography>
                <Typography {...liContentProps}>
                  Questions reflect the specific standards and expectations of
                  the curriculum, eliminating guesswork.
                </Typography>
                <Typography {...liProps}>Enhanced Engagement: </Typography>
                <Typography {...liContentProps}>
                  The variety and relevance of the questions keep students
                  motivated, breaking the monotony of textbook exercises.
                </Typography>
              </Typography>
            </Box>
            <Box sx={btnBoxStyles}>
              <Button
                variant="contained"
                {...btnProps}
                onClick={() => navigate(`${process.env.PUBLIC_URL}`)}
              >
                Get Started 🚀
              </Button>
            </Box>
            <Box display="flex" flexDirection="column" sx={boxStyles}>
              <Typography fontWeight="bold" fontSize="17px">
                How Topical Past Papers Benefit Students
              </Typography>
              <Typography color="#647488" fontSize="14px" paddingBottom="15px">
                <br />
                By incorporating Topical Past Papers into their study routine,
                students can unlock numerous advantages:
              </Typography>
              <Typography component="ul" style={{ margin: '0' }}>
                <Typography {...liProps}>
                  Increased Practice Opportunities
                </Typography>
                <Typography {...liContentProps}>
                  With a vast pool of questions from each chapter, students can
                  practice more extensively, gaining confidence with every
                  solved problem.
                </Typography>
                <Typography {...liProps}>
                  Familiarity with Question Types
                </Typography>
                <Typography {...liContentProps}>
                  Exposure to various question formats prepares students for the
                  unexpected, ensuring they’re never caught off guard in exams.
                </Typography>
                <Typography {...liProps}>
                  Focus on High-Impact Topics
                </Typography>
                <Typography {...liContentProps}>
                  Topical Past Papers highlight frequently asked questions and
                  critical concepts, enabling students to prioritize their
                  preparation effectively.
                </Typography>
                <Typography {...liProps}>
                  Strengths and Weaknesses Analysis
                </Typography>
                <Typography {...liContentProps}>
                  By tracking their performance on each paper, students can
                  pinpoint areas of difficulty and focus on improving them.
                </Typography>
                <Typography {...liProps}>
                  Improved Retention of Concepts
                </Typography>
                <Typography {...liContentProps}>
                  The structured approach helps reinforce understanding of key
                  concepts and sub-topics, laying a strong foundation for
                  long-term success.
                </Typography>
              </Typography>
            </Box>
            <Box display="flex" flexDirection="column" sx={boxStyles}>
              <Typography fontWeight="bold" fontSize="17px">
                The Ultimate Outcome: Exam Success
              </Typography>
              <Typography color="#647488" fontSize="14px" paddingBottom="15px">
                <br />
                What’s the end result? Students who practice with Topical Past
                Papers achieve:
              </Typography>
              <Typography component="ul" style={{ margin: '0' }}>
                <Typography {...liProps}>
                  A deep understanding of each chapter.
                </Typography>
                <Typography {...liProps}>
                  Greater confidence in tackling a variety of question types.
                </Typography>
                <Typography {...liProps}>
                  Enhanced ability to recall and apply concepts under exam
                  conditions.
                </Typography>
                <Typography {...liProps}>
                  Improved scores in both formative and summative assessments.
                </Typography>
              </Typography>
              <Typography color="#647488" fontSize="14px" marginTop="10px">
                In short, these papers empower students to approach their exams
                with unparalleled clarity and competence.
              </Typography>
            </Box>
            <Box sx={btnBoxStyles}>
              <Button
                variant="contained"
                {...btnProps}
                onClick={() => navigate(`${process.env.PUBLIC_URL}`)}
              >
                Get Started 🚀
              </Button>
            </Box>
            <Box display="flex" flexDirection="column" sx={boxStyles}>
              <Typography fontWeight="bold" fontSize="17px">
                Why Choose Our Topical Past Papers?
              </Typography>
              <Typography color="#647488" fontSize="14px" paddingBottom="15px">
                <br />
                Our Topical Past Papers are meticulously crafted to ensure the
                highest standards of quality and relevance. Designed by subject
                experts, they cater to students following the IGCSE Cambridge
                curriculum. If you&apos;re aiming for top grades in STEM
                subjects, these papers are tailored to meet your needs.
              </Typography>
            </Box>
            <Box display="flex" flexDirection="column" sx={boxStyles}>
              <Typography fontWeight="bold" fontSize="17px">
                Get Started Today
              </Typography>
              <Typography color="#647488" fontSize="14px" paddingBottom="15px">
                <br />
                We believe that success in exams doesn’t come from mere
                luck—it’s the result of consistent, targeted practice. With
                Topical Past Papers, we’ve made this process more effective,
                engaging, and results-driven. <br />
                <br /> Ready to take your exam preparation to the next level?
                Explore our collection of Topical Past Papers today and unlock
                your potential. <br />
                <br />
                <strong>
                  {' '}
                  Practice smarter. Prepare better. Achieve excellence.
                </strong>{' '}
                <br />
                <br /> Let’s transform your learning journey together!
              </Typography>
            </Box>
          </Container>
        </Box>
      </Box>
      <div style={{ height: '30px' }} />
      <Box
        sx={{
          background: `url('${process.env.PUBLIC_URL}/images/shapes/wave-with-gadgets.png') bottom left/contain repeat-x`,
          height: { xs: '100px', sm: '186px' },
        }}
      />
      <Footer />
    </>
  );
};

export default BlogPage;
